import React, { useEffect, useState } from "react";

import LightSelect from "../../CompanyView/LightSelect";
import {
  isLiveCommission,
  isLiveBonus,
  isLiveFlat,
  isRungBanked,
  withDatedChange,
  ruleSummary,
  APPLIES_PRESETS,
  appliesLabel,
} from "../../../components/SalesBonus/moneyRules";
import { companyTodayStr, formatSetupDay } from "../../../helpers/companyTime";
import "./customize.css";

/**
 * The Setup tab — where an admin composes what a team earns.
 *
 * Two money cards use it. Anticipated earnings gets the pay rules (a cut of a
 * money metric, flat bonuses on counted things); Milestone bonuses gets the
 * ladder. Both write to the WORKSPACE's program, not to the card, because pay
 * is one truth: two admins with the card on different boards must never see
 * different money.
 *
 * THE SHAPE OF THIS PANEL
 * Every rule is a RECORD IN A LIST, not a sentence with controls embedded in
 * it. Collapsed, a rule is always the same three things — one line of plain
 * English, one grey line of detail, one kebab. Live rules and blank rules are
 * identical in shape; the earlier version drew them differently, which made a
 * short list look ragged and made the × wander to a different spot per row.
 * Open a rule and it becomes a form on a fixed label column, so every field
 * starts on the same pixel. Prose cannot align; a column can.
 *
 * WHAT PROTECTS THE MONEY
 * Nothing is locked. An earlier version made a live rule read-only, which was
 * a trap: pick the wrong metric and there was no way back out of it. Instead,
 * EDITING A LIVE VALUE SUMMONS A START DATE. The old rate is kept in history
 * and only work from that date forward earns the new one, so raising commission
 * in August cannot re-price July.
 *
 * NOTHING here shows anyone's progress. This panel writes the rules for the
 * WHOLE workspace, so a rung that one rep has already cleared is not a fact
 * about the rung — the next rep hasn't cleared it. Per-person state (banked,
 * reached, earned) belongs on the card, which is per player; showing it here
 * meant the panel described whichever player's numbers happened to be loaded.
 *
 * Dropdowns are LightSelect, the same light portalled menu the rest of the app
 * uses. A native <select> renders the OS menu, which reads as foreign here.
 */

const Icon = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const IcKebab = (
  <>
    <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
  </>
);

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const today = () => new Date().toISOString().slice(0, 10);
const dollars = (v) => `$${n(v).toLocaleString()}`;

/* A metric only offers an inner condition when its entries carry a value that
   GROUPS — Memberships records a plan on every entry, so a handful of plans
   cover many entries. Customer names never group, so they never appear. */
const conditionsFor = (metric) => (metric && Array.isArray(metric.values) && metric.values.length ? metric.values : null);

const Select = ({ value, options, onChange, placeholder = "Choose a metric" }) => (
  <LightSelect
    width="100%"
    height={36}
    fontSize={12.5}
    radius={8}
    placeholder={placeholder}
    value={value || ""}
    options={options}
    onChange={(o) => onChange(o.value || null)}
  />
);

const metricOptions = (metrics, current) => {
  const opts = metrics.map((m) => ({ value: m.id, label: m.label }));
  // A rule can outlive its metric (admins disable KPIs). Keep the broken
  // reference selectable instead of silently snapping to another metric.
  if (current && !metrics.some((m) => m.id === current)) opts.push({ value: current, label: "Metric removed" });
  return opts;
};

/* When a bonus pays: the whole year, one quarter/half, or a typed range. A row
   of presets fills the dates for you; Custom range reveals two date inputs. The
   rule then only counts what falls inside that window, so "Q1–Q3 bonus" and a
   separate "Q4 bonus" stack up across the year. */
/* Clicking anywhere on a date field drops the calendar straight down, instead
   of making people tab through the dd / mm / yyyy segments. showPicker() needs
   a user gesture — a click is one — and is a no-op where unsupported. */
const openCalendar = (e) => { try { e.currentTarget.showPicker(); } catch (_) { /* older browser */ } };

/** Native date input with our own label — avoids Chrome's ", January, 01, 2026". */
const SetupDayInput = ({ value, "aria-label": ariaLabel, ...rest }) => (
  <label className="gmbw-datefield">
    <span className={`gmbw-datefield-txt${value ? "" : " is-empty"}`} aria-hidden>
      {value ? formatSetupDay(value) : "Pick a date"}
    </span>
    <input
      type="date"
      className="gmbw-datefield-in"
      aria-label={ariaLabel}
      value={value}
      onClick={openCalendar}
      {...rest}
    />
  </label>
);

const AppliesTo = ({ value, onChange }) => {
  const from = (value && value.from) || "";
  const to = (value && value.to) || "";
  const todayStr = companyTodayStr();
  const noFuture = (d) => (d && d > todayStr ? todayStr : d);
  const setFrom = (raw) => {
    const f = noFuture(raw);
    const nextTo = to && f && f > to ? f : to; // start ahead of end → bump end up
    onChange({ preset: "custom", from: f, to: nextTo });
  };
  const setTo = (raw) => {
    const t = noFuture(raw);
    const nextFrom = from && t && t < from ? t : from; // end before start → pull start down
    onChange({ preset: "custom", from: nextFrom, to: t });
  };
  return (
    <>
      <span className="gmbw-glab">Date range</span>
      <div className="gmbw-applies">
        {/* Just a plain date range — from → to. No quarter/half presets. */}
        <div className="gmbw-range">
          <SetupDayInput
            aria-label="Start date"
            value={from}
            max={todayStr}
            onChange={(e) => setFrom(e.target.value)}
          />
          <span className="gmbw-range-sep">→</span>
          <SetupDayInput
            aria-label="End date"
            value={to}
            min={from || undefined}
            max={todayStr}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>
    </>
  );
};

/* ── the collapsed row every rule shares ────────────────────────────────── */

const Row = ({ title, meta, unset, menu, onMenu, onOpen, onDuplicate, onRemove, name }) => (
  <div className="gmbw-rule">
    <button type="button" className="gmbw-rulerow" onClick={onOpen}>
      <span className="gmbw-rulerow-t">{title}</span>
      <span className={`gmbw-rulerow-m${unset ? " unset" : ""}`}>{meta}</span>
    </button>
    <button type="button" className="gmbw-kebab" aria-label={`More for ${name}`} onClick={onMenu}>
      <Icon d={IcKebab} size={15} />
    </button>
    {menu && (
      <div className="gmbw-menu">
        <button type="button" className="gmbw-mi" onClick={onOpen}>Edit</button>
        <button type="button" className="gmbw-mi" onClick={onDuplicate}>Duplicate</button>
        <button type="button" className="gmbw-mi danger" onClick={onRemove}>Remove</button>
      </div>
    )}
  </div>
);

/** The shell every open rule shares: heading, the label column, actions. */
const Editor = ({ live, children, tip, onRemove, onCancel, onDone }) => (
  <div className="gmbw-editor">
    <div className="gmbw-editor-h">
      <b>Editing</b>
      <span className="gmbw-livetag">{live ? "live — pays now" : "not paying yet"}</span>
    </div>
    <div className="gmbw-grid">{children}</div>
    {tip && <p className="gmbw-tip">{tip}</p>}
    <div className="gmbw-editor-a">
      <button type="button" className="gmbw-remove" onClick={onRemove}>Remove this rule</button>
      <button type="button" className="gmbw-sm ghost" onClick={onCancel}>Cancel</button>
      <button type="button" className="gmbw-sm" onClick={onDone}>Done</button>
    </div>
  </div>
);

/* ── commission: X% of a money metric ───────────────────────────────────── */

const summariseCommission = (rule, moneyMetrics) => {
  const label = moneyMetrics.find((m) => m.id === rule.kpiId);
  if (!isLiveCommission(rule)) {
    return { title: "Not set up yet", meta: "Add a rate and a metric to make it pay", unset: true };
  }
  // ruleSummary only mentions a rate change while it still touches the
  // current month; a rule fully on its new rate reads plainly. A custom
  // name shows next, so a renamed cut is visible at a glance in the list.
  const split = ruleSummary(rule, "pct");
  let meta = "Paying now";
  if (split) meta = split;
  else if (rule.label) meta = `Shows on the card as “${rule.label}”`;
  return {
    title: `${rule.pct}% of ${label ? label.label : "Metric removed"}`,
    meta,
    unset: false,
  };
};

const CommissionEditor = ({ rule, moneyMetrics, onCommit, onCancel, onRemove }) => {
  const [pct, setPct] = useState(n(rule.pct) === 0 ? "" : String(rule.pct));
  const [kpiId, setKpiId] = useState(rule.kpiId || null);
  const [label, setLabel] = useState(rule.label || "");
  const [from, setFrom] = useState(today());
  const live = isLiveCommission(rule);
  // A start date only matters once the rule already pays AND the rate moved.
  const dated = live && n(pct) !== n(rule.pct);

  return (
    <Editor
      live={live}
      onCancel={onCancel}
      onRemove={onRemove}
      onDone={() => {
        const base = { ...rule, kpiId, label: label.trim() || null };
        onCommit(dated ? withDatedChange(base, n(pct), from, "pct") : { ...base, pct: n(pct) });
      }}
      tip={dated
        ? `This rule already pays, so a start date appeared. Months before it stay at ${rule.pct}% — nothing already earned is recalculated.`
        : null}
    >
      <label htmlFor="gmbw-c-pct">Pays</label>
      <span className="gmbw-inline">
        <input
          id="gmbw-c-pct"
          type="number"
          className="gmbw-in num"
          value={pct}
          placeholder="1"
          min="0"
          max="100"
          step="0.1"
          onChange={(e) => setPct(e.target.value)}
        />
        <span>%</span>
      </span>

      <span className="gmbw-glab">Of</span>
      <Select value={kpiId} options={metricOptions(moneyMetrics, kpiId)} onChange={setKpiId} />

      <label htmlFor="gmbw-c-lab">What to call it on the card</label>
      <input
        id="gmbw-c-lab"
        type="text"
        className="gmbw-in"
        value={label}
        maxLength={60}
        placeholder="Commission"
        onChange={(e) => setLabel(e.target.value)}
      />

      {dated && (
        <>
          <label htmlFor="gmbw-c-from">Starting</label>
          <input id="gmbw-c-from" type="date" className="gmbw-in" value={from} onChange={(e) => setFrom(e.target.value)} />
        </>
      )}
    </Editor>
  );
};

/* ── bonus: $X for every counted thing, optionally one value inside it ───── */

const summariseBonus = (rule, countMetrics) => {
  const metric = countMetrics.find((m) => m.id === rule.kpiId);
  const mLabel = metric ? metric.label : "Metric removed";
  if (!isLiveBonus(rule)) {
    return { title: "Not set up yet", meta: "Add an amount and a metric to make it pay", unset: true };
  }
  const split = ruleSummary(rule, "amount");
  let meta = "Paying now";
  if (split) meta = split;
  else if (rule.label) meta = `Shows on the card as “${rule.label}”`;
  else meta = "No name set — the card falls back to the metric name";
  const win = appliesLabel(rule.appliesTo);
  if (win !== "Whole year") meta = `${meta} · ${win}`;
  return {
    title: `${dollars(rule.amount)} for every ${mLabel}${rule.where ? ` on ${rule.where}` : ""}`,
    meta,
    unset: false,
  };
};

const BonusEditor = ({ rule, countMetrics, onCommit, onCancel, onRemove }) => {
  const [amount, setAmount] = useState(n(rule.amount) === 0 ? "" : String(rule.amount));
  const [kpiId, setKpiId] = useState(rule.kpiId || null);
  const [where, setWhere] = useState(rule.where || "");
  const [label, setLabel] = useState(rule.label || "");
  const [appliesTo, setAppliesTo] = useState(rule.appliesTo || { preset: "year" });
  const [from, setFrom] = useState(today());
  const live = isLiveBonus(rule);
  const dated = live && n(amount) !== n(rule.amount);
  const metric = countMetrics.find((m) => m.id === kpiId);
  const conditions = conditionsFor(metric);
  const detail = (metric && metric.detail) || "type";

  let tip = null;
  if (dated) {
    tip = `This rule already pays, so a start date appeared. Months before it stay at ${dollars(rule.amount)} — nothing already earned is recalculated.`;
  } else if (conditions) {
    tip = `${metric.label} records a ${detail} on every entry, so one bonus can pay on just one of them. Spelling variants are grouped together.`;
  }

  return (
    <Editor
      live={live}
      onCancel={onCancel}
      onRemove={onRemove}
      tip={tip}
      onDone={() => {
        const base = { ...rule, kpiId, where: where || null, label: label.trim() || null, appliesTo };
        onCommit(dated ? withDatedChange(base, n(amount), from, "amount") : { ...base, amount: n(amount) });
      }}
    >
      <label htmlFor="gmbw-b-amt">Pays</label>
      <span className="gmbw-inline">
        <span>$</span>
        <input
          id="gmbw-b-amt"
          type="number"
          className="gmbw-in num"
          value={amount}
          placeholder="10"
          min="0"
          onChange={(e) => setAmount(e.target.value)}
        />
      </span>

      <span className="gmbw-glab">Counting</span>
      <Select
        value={kpiId}
        options={metricOptions(countMetrics, kpiId)}
        onChange={(id) => { setKpiId(id); setWhere(""); }}
      />

      {conditions && (
        <>
          <span className="gmbw-glab">Only on</span>
          <Select
            value={where}
            placeholder={`Any ${detail}`}
            options={[
              { value: "", label: `Any ${detail}` },
              ...conditions.map((v) => ({ value: v.value, label: v.count != null ? `${v.value} (${v.count})` : v.value })),
            ]}
            onChange={(v) => setWhere(v || "")}
          />
        </>
      )}

      <AppliesTo value={appliesTo} onChange={setAppliesTo} />

      <label htmlFor="gmbw-b-lab">What to call it on the card</label>
      <input
        id="gmbw-b-lab"
        type="text"
        className="gmbw-in"
        value={label}
        maxLength={60}
        placeholder={where && metric ? `${where} signups` : (metric && metric.label) || "Bonus"}
        onChange={(e) => setLabel(e.target.value)}
      />

      {dated && (
        <>
          <label htmlFor="gmbw-b-from">Starting</label>
          <input id="gmbw-b-from" type="date" className="gmbw-in" value={from} onChange={(e) => setFrom(e.target.value)} />
        </>
      )}
    </Editor>
  );
};

/* ── a section of rules: heading, rows, one open editor, add button ──────── */

const useRuleList = () => {
  const [open, setOpen] = useState(null); // "c2" | "b0" | "m1"
  const [menu, setMenu] = useState(null);
  // An open kebab menu must dismiss on any click that isn't in it. Closing only
  // by re-clicking the same kebab reads as a stuck menu.
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (!e.target.closest || !e.target.closest(".gmbw-menu, .gmbw-kebab")) setMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu]);
  return { open, setOpen, menu, setMenu };
};

const SectionHead = ({ label, count, gap }) => (
  <div className={`gmbw-sechead${gap ? " gap" : ""}`}>
    <span>{label}</span>
    <span className="gmbw-count">{count === 1 ? "1 rule" : `${count} rules`}</span>
  </div>
);

/* ── flat bonus: not from a metric — a name + an amount you just type ─────── */

const CADENCE_LABELS = { year: "per year", once: "one time", month: "per month" };
const cadenceLabel = (per) => CADENCE_LABELS[per] || "per month";

const summariseFlat = (rule) => {
  if (!isLiveFlat(rule)) return { title: "Not set up yet", meta: "Give it a name to make it show", unset: true };
  const win = appliesLabel(rule.appliesTo);
  const winSuffix = win !== "Whole year" ? ` · ${win}` : "";
  if (rule.informational) {
    return { title: rule.label, meta: `${rule.note || "Informational — shown as context, not added"}${winSuffix}`, unset: false };
  }
  return { title: `${rule.label} · ${dollars(rule.amount)}`, meta: `${rule.note || `Flat, ${cadenceLabel(rule.per)}`}${winSuffix}`, unset: false };
};

const PER_OPTIONS = [
  { value: "month", label: "per month" },
  { value: "year", label: "per year" },
  { value: "once", label: "one time" },
];

const FlatEditor = ({ rule, onCommit, onCancel, onRemove }) => {
  const [label, setLabel] = useState(rule.label || "");
  const [amount, setAmount] = useState(n(rule.amount) === 0 ? "" : String(rule.amount));
  const [per, setPer] = useState(rule.per || "month");
  const [note, setNote] = useState(rule.note || "");
  const [appliesTo, setAppliesTo] = useState(rule.appliesTo || { preset: "year" });
  const [info, setInfo] = useState(!!rule.informational);

  return (
    <Editor
      live={isLiveFlat(rule)}
      onCancel={onCancel}
      onRemove={onRemove}
      tip={info ? "Shown on the card as context — it is not added to the total." : null}
      onDone={() => onCommit({ label: label.trim(), amount: n(amount), per, note: note.trim() || null, appliesTo, informational: info })}
    >
      <label htmlFor="gmbw-f-l">Name</label>
      <input id="gmbw-f-l" type="text" className="gmbw-in" value={label} maxLength={60} placeholder="Loyalty bonus" onChange={(e) => setLabel(e.target.value)} />

      <label htmlFor="gmbw-f-a">Amount</label>
      <span className="gmbw-inline">
        <span>$</span>
        <input id="gmbw-f-a" type="number" className="gmbw-in num" value={amount} placeholder="500" min="0" onChange={(e) => setAmount(e.target.value)} />
      </span>

      <span className="gmbw-glab">Every</span>
      <Select value={per} options={PER_OPTIONS} onChange={(v) => setPer(v || "month")} />

      <AppliesTo value={appliesTo} onChange={setAppliesTo} />

      <label htmlFor="gmbw-f-n">Note</label>
      <input id="gmbw-f-n" type="text" className="gmbw-in" value={note} maxLength={80} placeholder="flat, every month" onChange={(e) => setNote(e.target.value)} />

      <span className="gmbw-glab">Type</span>
      <label className="gmbw-check">
        <input type="checkbox" checked={info} onChange={(e) => setInfo(e.target.checked)} />
        <span>Show only — put it on the card, but don’t add it to the total</span>
      </label>
    </Editor>
  );
};

/* ── the two Setup panels ───────────────────────────────────────────────── */

export const EarningsSetup = ({ program, metrics, onChange }) => {
  const { open, setOpen, menu, setMenu } = useRuleList();
  // Show EVERY metric in both pickers — the admin decides what a rule pays on,
  // not us. We used to gate commission to "$" metrics and bonuses to "#" only,
  // which hid most of a workspace's metrics behind our own judgment. Countable
  // (#) metrics still sort first in the counting list, since a bonus per entry
  // reads most naturally on them.
  const moneyMetrics = metrics;
  const countMetrics = [...metrics].sort((a, b) => {
    const rank = (m) => (m.kind === "#" ? 0 : 1);
    return rank(a) - rank(b) || a.label.localeCompare(b.label);
  });

  const { commissions } = program;
  const flats = program.flats || [];
  const setCommissions = (next) => onChange({ ...program, commissions: next });
  const setBonuses = (next) => onChange({ ...program, bonuses: next });
  const setFlats = (next) => onChange({ ...program, flats: next });
  const close = () => { setOpen(null); setMenu(null); };
  const at = (list, i, next) => list.map((r, k) => (k === i ? next : r));
  const without = (list, i) => list.filter((r, k) => k !== i);

  const blankCut = { kpiId: moneyMetrics[0] ? moneyMetrics[0].id : null, pct: "", label: "", effFrom: null, history: [] };
  const blankBonus = { kpiId: null, where: null, amount: "", label: "", effFrom: null, history: [] };
  const blankFlat = { label: "", amount: "", per: "month", note: "", informational: false };

  return (
    <>
      {/* Each section is a container, the same quiet card the create-component
          panel uses. Flat siblings ran together into one long list where the
          heads were the only separation — and they were faint. */}
      <div className="gmbw-secbox">
      <SectionHead label="Commission" count={commissions.length} />
      {commissions.map((rule, i) => {
        const key = `c${i}`;
        if (open === key) {
          return (
            <CommissionEditor
              // eslint-disable-next-line react/no-array-index-key
              key={key}
              rule={rule}
              moneyMetrics={moneyMetrics}
              onCancel={close}
              onRemove={() => { setCommissions(without(commissions, i)); close(); }}
              onCommit={(next) => { setCommissions(at(commissions, i, next)); close(); }}
            />
          );
        }
        const s = summariseCommission(rule, moneyMetrics);
        return (
          <Row
            // eslint-disable-next-line react/no-array-index-key
            key={key}
            {...s}
            name={`commission ${i + 1}`}
            menu={menu === key}
            onMenu={() => setMenu(menu === key ? null : key)}
            onOpen={() => { setOpen(key); setMenu(null); }}
            onDuplicate={() => { setCommissions([...commissions, { ...rule, label: "", effFrom: null, history: [] }]); setMenu(null); setOpen(`c${commissions.length}`); }}
            onRemove={() => { setCommissions(without(commissions, i)); close(); }}
          />
        );
      })}
      <button
        type="button"
        className="gmbw-addrule"
        onClick={() => { setCommissions([...commissions, blankCut]); setOpen(`c${commissions.length}`); setMenu(null); }}
      >
        {commissions.length ? "Add another cut" : "Add a commission"}
      </button>
      {!moneyMetrics.length && (
        <p className="gmbw-hint">No money metric in this workspace yet — add one under Metrics.</p>
      )}

      </div>

      <div className="gmbw-secbox">
      <SectionHead label="Bonuses" count={program.bonuses.length} />
      {program.bonuses.map((rule, i) => {
        const key = `b${i}`;
        if (open === key) {
          return (
            <BonusEditor
              // eslint-disable-next-line react/no-array-index-key
              key={key}
              rule={rule}
              countMetrics={countMetrics}
              onCancel={close}
              onRemove={() => { setBonuses(without(program.bonuses, i)); close(); }}
              onCommit={(next) => { setBonuses(at(program.bonuses, i, next)); close(); }}
            />
          );
        }
        const s = summariseBonus(rule, countMetrics);
        return (
          <Row
            // eslint-disable-next-line react/no-array-index-key
            key={key}
            {...s}
            name={`bonus ${i + 1}`}
            menu={menu === key}
            onMenu={() => setMenu(menu === key ? null : key)}
            onOpen={() => { setOpen(key); setMenu(null); }}
            onDuplicate={() => { setBonuses([...program.bonuses, { ...rule, label: "", effFrom: null, history: [] }]); setMenu(null); setOpen(`b${program.bonuses.length}`); }}
            onRemove={() => { setBonuses(without(program.bonuses, i)); close(); }}
          />
        );
      })}
      <button
        type="button"
        className="gmbw-addrule"
        onClick={() => { setBonuses([...program.bonuses, blankBonus]); setOpen(`b${program.bonuses.length}`); setMenu(null); }}
      >
        {program.bonuses.length ? "Add another bonus" : "Add a bonus"}
      </button>

      </div>

      <div className="gmbw-secbox">
      <SectionHead label="Flat bonuses" count={flats.length} />
      {flats.map((rule, i) => {
        const key = `f${i}`;
        if (open === key) {
          return (
            <FlatEditor
              // eslint-disable-next-line react/no-array-index-key
              key={key}
              rule={rule}
              onCancel={close}
              onRemove={() => { setFlats(without(flats, i)); close(); }}
              onCommit={(next) => { setFlats(at(flats, i, next)); close(); }}
            />
          );
        }
        const s = summariseFlat(rule);
        return (
          <Row
            // eslint-disable-next-line react/no-array-index-key
            key={key}
            {...s}
            name={`flat bonus ${i + 1}`}
            menu={menu === key}
            onMenu={() => setMenu(menu === key ? null : key)}
            onOpen={() => { setOpen(key); setMenu(null); }}
            onDuplicate={() => { setFlats([...flats, { ...rule }]); setMenu(null); setOpen(`f${flats.length}`); }}
            onRemove={() => { setFlats(without(flats, i)); close(); }}
          />
        );
      })}
      <button
        type="button"
        className="gmbw-addrule"
        onClick={() => { setFlats([...flats, blankFlat]); setOpen(`f${flats.length}`); setMenu(null); }}
      >
        {flats.length ? "Add another flat bonus" : "Add a flat bonus"}
      </button>

      </div>

      <p className="gmbw-foot">
        A line marked “show only” appears on the card for context — its amount is never added to the total.
      </p>
    </>
  );
};

export const LadderSetup = ({ program, metrics, ytd = 0, onChange }) => {
  const { open, setOpen, menu, setMenu } = useRuleList();
  // Every metric is selectable for the ladder too — no kind gating.
  const moneyMetrics = metrics;
  const rungs = program.milestones;

  const setRungs = (next) => onChange({ ...program, milestones: next });
  const close = () => { setOpen(null); setMenu(null); };
  // Rungs are a ladder, so they always read low to high no matter what order
  // they were typed in.
  const sortRungs = (list) => [...list].sort((a, b) => (n(a.target) || Infinity) - (n(b.target) || Infinity));

  return (
    <>
      <div className="gmbw-fld">
        <label id="gmbw-scored-l">What the milestones are measured on</label>
        <Select
          value={program.scoredOnKpiId}
          options={metricOptions(moneyMetrics, program.scoredOnKpiId)}
          onChange={(id) => onChange({ ...program, scoredOnKpiId: id })}
        />
        <p className="gmbw-hint">Measured year to date.</p>
      </div>

      <div className="gmbw-secbox">
      <SectionHead label="The ladder" count={rungs.length} />
      {rungs.map((rung, i) => {
        const key = `m${i}`;
        // Reached rungs used to render locked ("Banked") — now they stay
        // editable like any other rung, per request.
        const banked = isRungBanked(program, ytd, rung);
        if (open === key) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <RungEditor
              key={key}
              rung={rung}
              index={i}
              onCancel={close}
              onRemove={() => { setRungs(rungs.filter((r, k) => k !== i)); close(); }}
              onCommit={(next) => { setRungs(sortRungs(rungs.map((r, k) => (k === i ? next : r)))); close(); }}
            />
          );
        }
        const set = n(rung.target) > 0;
        let meta = "Give it a target and a bonus to make it count";
        if (set) meta = banked ? "Reached" : "Not reached yet";
        return (
          <Row
            // eslint-disable-next-line react/no-array-index-key
            key={key}
            title={set ? `Reach ${dollars(rung.target)} → pays ${dollars(rung.bonus)}` : "Empty rung"}
            meta={meta}
            unset={!set}
            name={`milestone ${i + 1}`}
            menu={menu === key}
            onMenu={() => setMenu(menu === key ? null : key)}
            onOpen={() => { setOpen(key); setMenu(null); }}
            onDuplicate={() => { setRungs([...rungs, { target: n(rung.target) * 2, bonus: n(rung.bonus) * 2 }]); setMenu(null); }}
            onRemove={() => { setRungs(rungs.filter((r, k) => k !== i)); close(); }}
          />
        );
      })}
      <button
        type="button"
        className="gmbw-addrule"
        onClick={() => {
          const last = rungs[rungs.length - 1];
          const t = n(last && last.target);
          const b = n(last && last.bonus);
          setRungs([...rungs, { target: t ? t * 2 : "", bonus: b ? b * 2 : "" }]);
          setOpen(`m${rungs.length}`);
          setMenu(null);
        }}
      >
        {rungs.length ? "Add another milestone" : "Add a milestone"}
      </button>
      </div>

      <p className="gmbw-foot">
        A milestone needs both a target and a bonus before it counts.
      </p>
    </>
  );
};

const RungEditor = ({ rung, index, onCommit, onCancel, onRemove }) => {
  const [target, setTarget] = useState(n(rung.target) === 0 ? "" : String(rung.target));
  const [bonus, setBonus] = useState(n(rung.bonus) === 0 ? "" : String(rung.bonus));
  const hints = [["50000", "150"], ["100000", "300"], ["200000", "600"]][index] || ["", ""];

  return (
    <Editor
      live={n(rung.target) > 0}
      onCancel={onCancel}
      onRemove={onRemove}
      onDone={() => onCommit({ target: n(target), bonus: n(bonus) })}
      tip="Paid once, the first time the year&rsquo;s total reaches that number."
    >
      <label htmlFor="gmbw-m-t">Reach</label>
      <span className="gmbw-inline">
        <span>$</span>
        <input id="gmbw-m-t" type="number" className="gmbw-in num wide" value={target} placeholder={hints[0]} min="0" onChange={(e) => setTarget(e.target.value)} />
      </span>

      <label htmlFor="gmbw-m-b">Pays</label>
      <span className="gmbw-inline">
        <span>$</span>
        <input id="gmbw-m-b" type="number" className="gmbw-in num wide" value={bonus} placeholder={hints[1]} min="0" onChange={(e) => setBonus(e.target.value)} />
      </span>
    </Editor>
  );
};
